/*
Turn a failed backend response into something a person can act on.

Three different error shapes can come back, and the old code understood none of
them — it did `errBody.message || errBody.error`, and since our own envelope
nests the message *inside* `error`, users were shown the literal string
"[object Object]" instead of the reason their solve was rejected.

  1. our envelope    { success: false, error: { code, message } }
  2. Zuplo/gateway   { type, title, status, detail, instance }   (problem+json)
  3. plain FastAPI   { detail: "..." }  or  { message: "..." }
*/

/** Pull a { code, message, details } out of whatever the server sent. */
export function parseBackendError(body, status, statusText) {
  let code = null;
  let message = "";
  let details = null;

  if (body && typeof body === "object") {
    const err = body.error;
    if (err && typeof err === "object") {
      // our standard envelope
      code = err.code || null;
      message = err.message || "";
      // `details` can ride inside the error envelope or, for plainer backends,
      // at the top level — e.g. { kind: 'feature', feature: 'road_network_costs' }.
      if (err.details && typeof err.details === "object") details = err.details;
    } else if (typeof err === "string") {
      message = err;
    }
    if (!details && body.details && typeof body.details === "object") details = body.details;
    if (!message && typeof body.message === "string") message = body.message;
    // Zuplo problem+json: `detail` is the human part, `title` the category.
    if (!message && typeof body.detail === "string") message = body.detail;
    if (!message && typeof body.title === "string") message = body.title;
  } else if (typeof body === "string") {
    message = body;
  }

  if (!message) message = statusText || `HTTP ${status}`;
  return { code, message, status, details };
}

/*
Known error codes, in the words of someone running a fleet rather than the words
of the service that raised them. Anything unmapped falls through to the server's
own message, which is still better than a status line.
*/
const BY_CODE = {
  CONCURRENCY_LIMIT:
    "You already have the maximum number of optimizations running. Wait for one to finish, or cancel it, then try again.",
  PROBLEM_TOO_LARGE:
    "This scenario is bigger than your plan allows. Reduce the number of locations or vehicles, or upgrade for larger runs.",
  PAYLOAD_TOO_LARGE:
    "This scenario is too large to send in one request. Reduce the number of locations, or the size of the travel-cost table.",
  PLAN_LIMIT_EXCEEDED:
    "This scenario uses a feature or a size that isn't included in your plan.",
  RUN_NOT_FOUND: "That optimization run no longer exists.",
  RESULTS_NOT_FOUND: "The results for that run aren't available yet.",
  /*
  The two travel-cost rejections. The run tracker's toast keeps only the first
  sentence of a long reason (useSolvePoller's trimReason, 180 chars), so each
  first sentence has to say what is wrong on its own; the fix follows. The
  button is named as its tooltip and accessible name begin ("Populate travel
  costs ..."), so a voice-control user can say it.

  UNCOSTED_TRAVEL_MATRIX fires when no pair of different locations has BOTH a
  cost and a duration (backend #348): the solver reads those two and treats a
  null in either as unreachable, so a cost-only or distance-only matrix, or one
  whose durations are all empty, routes nothing. A matrix of real zeros is
  accepted and solves, so the message must not say "zero".

  The remedy is Run itself. The website's check before a run (useTravelCostGapCheck)
  requires a cost and a travel time on every pair, so pressing Run again opens a
  dialog that says what is missing, calculates what can be calculated while
  keeping every value someone entered, and names what only they can enter. It
  used to point at "Populate travel costs", which recalculates everything and
  would overwrite typed distances and costs.
  */
  UNCOSTED_TRAVEL_MATRIX:
    "None of your travel costs has both a cost and a travel time, so no route can be planned. " +
    "Run the optimization again: it shows what's missing and fills in what it can, keeping the values you entered.",
  MISSING_TRAVEL_COSTS:
    "Your travel costs are empty. Fill them in with “Populate travel costs” on the Travel Costs table, then solve.",
};

/*
The same rejection for an x-y scenario. Its travel costs are recalculated from the
coordinates on every run, replacing any entered value, and it doesn't stop at the
travel-cost dialog, so neither "keeping the values you entered" nor "it shows
what's missing" is true. Only locations without numeric x and y coordinates can
leave it with nothing to route. Chosen where the run is submitted
(solveOptimization), which knows the scenario's coordinate system.
*/
export const UNCOSTED_TRAVEL_MATRIX_XY_MESSAGE =
  "None of your travel costs has both a cost and a travel time, so no route can be planned. " +
  "Check that your locations have numeric x and y coordinates, then run the optimization again.";

/*
Distance-method names as a person picked them in the cost settings, so a
fallback suggestion reads like the control they have to change rather than an
enum from the wire.
*/
const METHOD_LABELS = {
  geo: "straight-line",
  haversine: "straight-line",
  manhattan: "Manhattan",
  euclidean: "straight-line",
  driving: "road-network",
};

/**
 * The road-network engine is down, and the backend told us which methods would
 * work instead (`details.fallback_methods`). Naming them matters: the generic
 * "temporarily unavailable" invites a retry that cannot succeed, while a person
 * who switches the distance method solves today.
 */
function roadNetworkUnavailableMessage(err) {
  const raw = (err && err.details && err.details.fallback_methods) || [];
  const labels = [];
  for (const method of raw) {
    const label = METHOD_LABELS[method] || method;
    if (label && !labels.includes(label)) labels.push(label);
  }
  if (!labels.length) {
    return "Road-network driving distances are unavailable right now. Switch to another distance method in cost settings to solve in the meantime.";
  }
  const list =
    labels.length === 1
      ? labels[0]
      : `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
  return `Road-network driving distances are unavailable right now. Switch to ${list} distances in cost settings to solve in the meantime.`;
}

/*
When a plan-limit rejection names the specific feature that's gated, say what
that feature is and what to do about it — far more useful than the generic
"a feature or a size that isn't included in your plan." Keyed by the backend's
`details.feature`.
*/
const BY_FEATURE = {
  road_network_costs:
    "Road-network driving distances are a Professional-plan feature. Upgrade to calculate real road distances, or switch to a straight-line method.",
  fixed_assignments:
    "Pinning trips or locations to specific vehicles is a Professional-plan feature. Upgrade to lock assignments before you solve.",
};

/** The gated feature name from a plan-limit error, or null. */
export function planLimitFeature(err) {
  if (!err || err.code !== "PLAN_LIMIT_EXCEEDED") return null;
  const feature = err.feature || (err.details && err.details.feature) || null;
  return typeof feature === "string" ? feature : null;
}

/**
 * Whether this error is a plan limit the user could clear by upgrading — the
 * signal the UI uses to offer an "upgrade / see plans" call to action.
 * @param {Error|{code}} err
 */
export function isUpgradeableError(err) {
  return Boolean(err && err.code === "PLAN_LIMIT_EXCEEDED");
}

const BY_STATUS = {
  401: "You need to be signed in to do that.",
  403: "Your plan doesn't include that.",
  413: "This scenario is too large to send in one request.",
  429: "Too many requests right now. Wait a moment and try again.",
  // 500 has no entry above by design until now: an uncaught backend exception
  // used to fall through to "Something went wrong", which reads as a transient
  // blip and invites an endless retry. Say what it actually is.
  500: "Something went wrong on our end. If it keeps happening, the details in your last change are worth sending us.",
  502: "The optimization service is temporarily unavailable. Please try again.",
  503: "The optimization service is temporarily unavailable. Please try again.",
  504: "The optimization service took too long to respond. Please try again.",
};

/**
 * A message worth showing a user.
 * @param {Error|{code,message,status}} err - a thrown request error, or a parsed one.
 */
export function friendlyBackendMessage(err) {
  if (!err) return "Something went wrong. Please try again.";
  const code = err.code || null;
  const status = err.status || null;
  const raw = err.backendMessage || err.message || "";

  // Prefer a feature-specific message when the plan limit names a feature.
  const feature = planLimitFeature(err);
  if (feature && BY_FEATURE[feature]) return BY_FEATURE[feature];

  // The road-network engine names its own way out, so build from the payload
  // rather than from a fixed string.
  if (code === "ROAD_NETWORK_UNAVAILABLE") return roadNetworkUnavailableMessage(err);

  if (code && BY_CODE[code]) return BY_CODE[code];
  if (status && BY_STATUS[status]) return BY_STATUS[status];
  // Fall back to whatever the server said, as long as it isn't a stringified object.
  if (raw && !raw.includes("[object")) return raw;
  return "Something went wrong. Please try again.";
}
